import { Component, lazy, Suspense, type ComponentType, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import LiveChat from "@/components/LiveChat";
import CookieConsent from "@/components/CookieConsent";
import { useVisitorTracking } from "@/hooks/use-visitor-tracking";

const CHUNK_RELOAD_KEY = "lecando-chunk-reload-attempted";

const isModuleLoadError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);

  return /Importing a module script failed|Failed to fetch dynamically imported module|Loading chunk \d+ failed|ChunkLoadError/i.test(message);
};

const reloadForFreshAssets = () => {
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === "true") return false;

  sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");
  window.location.reload();
  return true;
};

const safeLazy = <T extends ComponentType<unknown>>(loader: () => Promise<{ default: T }>) =>
  lazy(() =>
    loader().catch((error) => {
      if (isModuleLoadError(error) && reloadForFreshAssets()) {
        return new Promise<{ default: T }>(() => undefined);
      }

      throw error;
    }),
  );

class ChunkErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError(error: unknown) {
    if (isModuleLoadError(error) && reloadForFreshAssets()) {
      return { hasError: false };
    }

    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
          <div className="max-w-md text-center space-y-4">
            <h1 className="text-2xl font-semibold text-foreground">Something went wrong</h1>
            <p className="text-muted-foreground">Please refresh the page to load the latest version.</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Lazy load route components for code splitting, with stale chunk recovery after deployments
const Index = safeLazy(() => import("./pages/Index"));
const Auth = safeLazy(() => import("./pages/Auth"));
const ProjectSelection = safeLazy(() => import("./pages/ProjectSelection"));
const InvestFlow = safeLazy(() => import("./pages/InvestFlow"));
const Dashboard = safeLazy(() => import("./pages/Dashboard"));
const Admin = safeLazy(() => import("./pages/Admin"));
const Privacy = safeLazy(() => import("./pages/Privacy"));
const Terms = safeLazy(() => import("./pages/Terms"));
const Cookies = safeLazy(() => import("./pages/Cookies"));
const ResetPassword = safeLazy(() => import("./pages/ResetPassword"));
const NotFound = safeLazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

// Wrapper component to use hooks inside Router context
const AppRoutes = () => {
  // Track visitor analytics
  useVisitorTracking();

  return (
    <>
      <ChunkErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/projects" element={<ProjectSelection />} />
            <Route path="/invest/:projectId" element={<InvestFlow />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ChunkErrorBoundary>
      <LiveChat />
      <CookieConsent />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
